import React, { useCallback, useEffect, useState } from 'react';
import { Card, Grid, Box, Switch, FormControlLabel, Typography, Tooltip, Backdrop, IconButton, Snackbar, Alert, Stack, Icon, FormControl, OutlinedInput, InputLabel } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import { useDispatch, useSelector } from 'react-redux';
import { PmConfigType } from './pmConfigType';
import { useHistory, useParams } from 'react-router';
import whiteBackIcon from '../../icons/whiteBackIcon.svg';
import { useStyles } from './styles';
import AddIcon from '../../icons/addcard.png';
import EditIcon from '../../icons/editIcon.svg';
import DeleteIcon from '../../icons/deleteIcon.svg';
import DeleteModal from './components/DeleteModal';
import { MaintenanceEmergencyCriteriaDto, Role } from '@inosio/payments-sdk';
import SpinnerModal from '../../shared/Modal/SpinnerModal';
import MaintenanceCriteriaModal, { MaintenanceCriteriaModalType } from './components/MaintenanceCriteriaModal';
import MaintenancePoliciesModal from './components/MaintenancePoliciesModal';
import debounce from 'lodash.debounce';
import { selectCurrentUser } from '../../shared/sagas/auth/authSlice';
import ReviewOtpIcon from '../../icons/ReviewOtpIcon.svg';
import { E164PhoneRegex, EmailRegex } from '../../utils/regex';
import {
  clearPropertyGroupConfigSettingsFlags,
  getAllPropertyGroupsConfigs,
  getUpdatePropertyGroupConfigs,
  selectPropertyGroupDetailsState,
  selectSpecificPropertyGroupConfigs,
} from '../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { PhoneNumberInput } from '../../shared/PhoneNumberInput';

export default function MaintenanceConfigSettings() {
  const { uuid: propertyGroupIdParam } = useParams<{ uuid: string }>();
  const classes = useStyles();
  const history = useHistory();
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [copySnackBarOpen, setCopySnackBarOpen] = useState(false);

  const currentUser = useSelector(selectCurrentUser);

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackBarOpen(false);
    setCopySnackBarOpen(false);
  };

  const dispatch = useDispatch();
  useEffect(() => {
    resetPage();
  }, []);

  const {
    selectedPropertyGroupAssignedConfigs,
    isFetchingAllPropertyGroupConfigsSuccess,
    UpdatingPropertyGroupConfigsError,
    isPropertyGroupAssignedConfigsRefreshRequired,
    isUpdatingPropertyGroupConfigsSuccess,
  } = useSelector(selectPropertyGroupDetailsState);

  useEffect(() => {
    dispatch({
      type: selectSpecificPropertyGroupConfigs.type,
      payload: {
        propertyGroupUuid: propertyGroupIdParam,
      },
    });
  }, [isFetchingAllPropertyGroupConfigsSuccess]);

  useEffect(() => {
    if (UpdatingPropertyGroupConfigsError.message?.length > 0) {
      setSnackBarOpen(true);
    }
  }, [UpdatingPropertyGroupConfigsError]);

  const [pmConfig, setPmConfig] = useState<PmConfigType>({});

  useEffect(() => {
    setPmConfig(selectedPropertyGroupAssignedConfigs);
    setEmergencyContactPhone(selectedPropertyGroupAssignedConfigs.MaintenanceEmergencyPhone ?? '');
    setContactName(selectedPropertyGroupAssignedConfigs.MaintenanceContactName ?? '');
    setContactEmail(selectedPropertyGroupAssignedConfigs.MaintenanceContactEmail ?? '');
    setContactPhone(selectedPropertyGroupAssignedConfigs.MaintenanceContactPhone ?? '');
  }, [selectedPropertyGroupAssignedConfigs]);

  const resetPage = () => {
    dispatch(clearPropertyGroupConfigSettingsFlags({}));
    setIsDeletePoliciesModalOpen(false);
    setIsPoliciesModalOpen(false);
    setIsDeleteCriteriaModalOpen(false);
    setIsCriteriaModalOpen(false);
    setIsSpinnerModalOpen(false);
  };

  useEffect(() => {
    if (isUpdatingPropertyGroupConfigsSuccess) {
      dispatch(clearPropertyGroupConfigSettingsFlags({}));
      if (isPropertyGroupAssignedConfigsRefreshRequired) dispatch(getAllPropertyGroupsConfigs());
      setIsDeletePoliciesModalOpen(false);
      setIsPoliciesModalOpen(false);
      setIsDeleteCriteriaModalOpen(false);
      setIsCriteriaModalOpen(false);
      setIsSpinnerModalOpen(false);
    }
  }, [isUpdatingPropertyGroupConfigsSuccess]);

  const [pendingConfig, setPendingConfig] = React.useState<any>(null);

  const handleChange = (ConfigTypeKey1: string, AssignedValue1: any, ConfigTypeKey2?: string, AssignedValue2?: any) => {
    let newPmConfig = {};

    if (ConfigTypeKey2 && AssignedValue2 !== undefined) {
      newPmConfig = {
        ...pmConfig,
        [ConfigTypeKey1]: AssignedValue1,
        [ConfigTypeKey2]: AssignedValue2,
      };
      setPendingConfig({
        ...pendingConfig,
        [ConfigTypeKey1]: AssignedValue1,
        [ConfigTypeKey2]: AssignedValue2,
      });
    } else {
      newPmConfig = {
        ...pmConfig,
        [ConfigTypeKey1]: AssignedValue1,
      };
      setPendingConfig({
        ...pendingConfig,
        [ConfigTypeKey1]: AssignedValue1,
      });
    }

    setPmConfig(newPmConfig);
  };

  const actuallyDispatchConfigUpdate = () => {
    if (pendingConfig) {
      dispatch(getUpdatePropertyGroupConfigs({ propertyGroupUuid: propertyGroupIdParam, productTypeId: 3, updatedPmConfig: pendingConfig }));
      setPendingConfig(null);
    }
  };

  const delayedConfigUpdate = useCallback(debounce(actuallyDispatchConfigUpdate, 750), [pendingConfig]);

  useEffect(() => {
    delayedConfigUpdate();
    return delayedConfigUpdate.cancel;
  }, [pendingConfig, delayedConfigUpdate]);

  const getTypography = (text: string, fontWeight: number, fontSize: string) => (
    <Typography
      sx={{
        fontFamily: 'Work Sans',
        fontWeight: { fontWeight },
        fontSize: { fontSize },
        lineHeight: '22px',
      }}
    >
      {text}
    </Typography>
  );

  const [isPoliciesModalOpen, setIsPoliciesModalOpen] = React.useState(false);
  const handleSavePolicies = (text: string) => {
    setPmConfig({ ...pmConfig, MaintenancePolicyText: text });
  };
  const [isDeletePoliciesModalOpen, setIsDeletePoliciesModalOpen] = React.useState(false);
  const handleConfirmDeletePoliciesModal = () => {
    setIsSpinnerModalOpen(true);
    dispatch(
      getUpdatePropertyGroupConfigs({
        propertyGroupUuid: propertyGroupIdParam,
        productTypeId: 3,
        updatedPmConfig: {
          MaintenancePolicyText: '',
        },
      }),
    );
    setPmConfig({ ...pmConfig, MaintenancePolicyText: '' });
  };

  const isValidPhoneNumber = (number: string) => {
    return E164PhoneRegex.test(number);
  };

  const [emergencyContactPhone, setEmergencyContactPhone] = React.useState('');
  const [emergencyContactPhoneError, setEmergencyContactPhoneError] = React.useState(false);
  const handleEmergencyContactPhone = (phone: string) => {
    setEmergencyContactPhone(phone.startsWith('+') ? phone : `+${phone}`);
  };

  const handleOnBlurEmergencyContact = () => {
    if (isValidPhoneNumber(emergencyContactPhone)) {
      setEmergencyContactPhoneError(false);
      dispatch(
        getUpdatePropertyGroupConfigs({
          propertyGroupUuid: propertyGroupIdParam,
          productTypeId: 3,
          updatedPmConfig: {
            MaintenanceEmergencyPhone: emergencyContactPhone,
          },
        }),
      );
    } else {
      setEmergencyContactPhoneError(true);
    }
  };

  const [contactName, setContactName] = React.useState('');
  const handleContactName = (name: string) => {
    setContactName(name);

    dispatch(
      getUpdatePropertyGroupConfigs({
        propertyGroupUuid: propertyGroupIdParam,
        productTypeId: 3,
        updatedPmConfig: {
          MaintenanceContactName: name,
        },
      }),
    );
  };

  const [contactEmail, setContactEmail] = React.useState('');
  const [contactEmailError, setContactEmailError] = React.useState(false);
  const handleContactEmail = (email: string) => {
    setContactEmail(email);

    if (email && EmailRegex.test(email)) {
      setContactEmailError(false);
    } else {
      setContactEmailError(true);
    }
  };
  const handleSaveContactEmail = (email: string) => {
    setContactEmail(email);

    if (email && EmailRegex.test(email)) {
      setContactEmailError(false);
      dispatch(
        getUpdatePropertyGroupConfigs({
          propertyGroupUuid: propertyGroupIdParam,
          productTypeId: 3,
          updatedPmConfig: {
            MaintenanceContactEmail: email,
          },
        }),
      );
    } else {
      setContactEmailError(true);
    }
  };

  const [contactPhone, setContactPhone] = React.useState('');
  const [contactPhoneError, setContactPhoneError] = React.useState(false);
  const handleContactPhone = (phone: string) => {
    setContactPhone(phone.startsWith('+') ? phone : `+${phone}`);
  };

  const handleOnBlurContactPhone = () => {
    if (isValidPhoneNumber(contactPhone)) {
      setContactPhoneError(false);
      dispatch(
        getUpdatePropertyGroupConfigs({
          propertyGroupUuid: propertyGroupIdParam,
          productTypeId: 3,
          updatedPmConfig: {
            MaintenanceContactPhone: contactPhone,
          },
        }),
      );
    } else {
      setContactPhoneError(true);
    }
  };

  const [criteriaModalType, setCriteriaModalType] = React.useState<MaintenanceCriteriaModalType>(MaintenanceCriteriaModalType.Create);
  const [isCriteriaModalOpen, setIsCriteriaModalOpen] = React.useState(false);
  const [selectedMEC, setSelectedMEC] = React.useState<MaintenanceEmergencyCriteriaDto>();
  const [isDeleteCriteriaModalOpen, setIsDeleteCriteriaModalOpen] = React.useState(false);
  const handleConfirmDeleteCriteriaModal = () => {
    setIsSpinnerModalOpen(true);
    selectedMEC &&
      dispatch(
        getUpdatePropertyGroupConfigs({
          propertyGroupUuid: propertyGroupIdParam,
          productTypeId: 3,
          updatedPmConfig: {
            MaintenanceEmergencyCriteria: [
              {
                ...selectedMEC,
                IsDeleting: true,
              },
            ],
          },
        }),
      );
  };

  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = React.useState(false);
  const [spinnerStatus, setSpinnerStatus] = React.useState<string | null>(null);
  const handleCloseSpinnerModal = () => {
    setIsSpinnerModalOpen(false);
    setIsPoliciesModalOpen(false);
  };

  return (
    <Grid item xs={12} sx={{ mt: 3.5 }}>
      <Card sx={{ display: 'flex', flexDirection: 'column', color: '#000'}}>
        <Box className={classes.headerContainer} sx={{ backgroundColor: '#A4243B' }}>
          <IconButton
            sx={{ marginRight: '10px', marginTop: '-4px' }}
            onClick={() => {
              resetPage();
              history.push(`/pmui/propertygroups/${propertyGroupIdParam}`);
            }}
          >
            <img src={whiteBackIcon} alt="" height="14px" />
          </IconButton>
          {pmConfig.MaintenanceProductPurchased && pmConfig.MaintenanceMasterFlag && (
            <FormControlLabel
              sx={{ color: '#FFF', ml: 0 }}
              control={
                <Switch
                  checked={!!pmConfig.MaintenanceProductEnabled}
                  onChange={(event) => handleChange(event.target.name, event.target.checked)}
                  name="MaintenanceProductEnabled"
                  disabled={currentUser?.currentProfile.role === Role.MaintenanceStaff}
                />
              }
              label={getTypography(selectedPropertyGroupAssignedConfigs.Name + ': Maintenance', 600, '24px')}
              labelPlacement="start"
            />
          )}
        </Box>

        <Grid container sx={{ p: 2, pt: 2, pb: 1, position: 'relative' }}>
          <Grid item xs={12} md={6}>
            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer} sx={{ justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getTypography('Emergency Criteria', 600, '18px')}

                  <Tooltip
                    title='These are brief descriptions of issues that will be displayed to tenants when they create a maintenance request to screen and detect emergency items. Ex: uncontrollable flooding. 2-3" of non-draining water. No AC and outside temp is greater than 90 degrees.'
                    placement="right"
                  >
                    <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                  </Tooltip>
                </Box>

                {currentUser?.currentProfile.role !== Role.MaintenanceStaff && (
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() => {
                        setSelectedMEC(undefined);
                        setCriteriaModalType(MaintenanceCriteriaModalType.Create);
                        setIsCriteriaModalOpen(true);
                      }}
                      sx={{ p: 0.8, mr: '-8px' }}
                    >
                      <Icon sx={{ width: 26, height: 26 }}>
                        <img
                          style={{
                            cursor: 'pointer',
                            width: 26,
                            height: 26,
                          }}
                          src={AddIcon}
                          alt="Add an item"
                        />
                      </Icon>
                    </IconButton>
                  </Stack>
                )}
              </Box>
              <Box
                className={classes.configBodyContainer}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  marginTop: '1px',
                  height: 150,
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: 7,
                  },
                  '&::-webkit-scrollbar-track': {
                    boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                    borderRadius: '24px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'darkgrey',
                    borderRadius: '24px',
                  },
                  mb: 1,
                }}
              >
                {selectedPropertyGroupAssignedConfigs?.MaintenanceEmergencyCriteria?.map((mec, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 1,
                      borderTop: index ? '3px solid #F1F0F5' : 'none',
                      marginLeft: '-18px',
                      marginRight: '-18px',
                      paddingLeft: '18px',
                      paddingRight: '18px',
                    }}
                  >
                    <div
                      style={{
                        fontFamily: 'Work Sans',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '18px',
                        color: '#14142B',
                        width: 'calc( 100% - 80px )',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      }}
                      dangerouslySetInnerHTML={{ __html: mec.CriteriaLabel ?? '' }}
                    />
                    <Box sx={{ width: 60, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <IconButton
                          onClick={() => {
                            setSelectedMEC(mec);
                            setCriteriaModalType(MaintenanceCriteriaModalType.Edit);
                            setIsCriteriaModalOpen(true);
                          }}
                          sx={{ p: 0.25, mr: '-2px' }}
                        >
                          <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                              style={{
                                cursor: 'pointer',
                                width: '14px',
                                height: '14px',
                              }}
                              src={currentUser?.currentProfile.role === Role.MaintenanceStaff ? ReviewOtpIcon : EditIcon}
                              alt="Edit"
                            />
                          </Icon>
                        </IconButton>
                      </Stack>
                      {currentUser?.currentProfile.role !== Role.MaintenanceStaff && (
                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                          <IconButton
                            onClick={() => {
                              setSelectedMEC(mec);
                              setIsDeleteCriteriaModalOpen(true);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <img
                                style={{
                                  cursor: 'pointer',
                                  width: '15px',
                                  height: '18px',
                                }}
                                src={DeleteIcon}
                                alt="Delete"
                              />
                            </Icon>
                          </IconButton>
                        </Stack>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer} sx={{ justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getTypography('Emergency Contact Number', 600, '18px')}

                  <Tooltip title="This is the phone number that a resident should contact in the event of a maintenance emergency. Ex: uncontrollable flooding." placement="right">
                    <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                  </Tooltip>
                </Box>
              </Box>
              <Box className={classes.configBodyContainer} sx={{ my: 1 }}>
                <Grid container sx={{ alignItems: 'center' }}>
                  <Grid item md={12} lg={7} sx={{ my: 1 }}>
                    {getTypography('Tenant Emergency Contact Number:', 400, '18px')}
                  </Grid>
                  <Grid item md={12} lg={5} sx={{ my: 1 }}>
                    <PhoneNumberInput
                      value={emergencyContactPhone}
                      showErrorMessage={emergencyContactPhoneError}
                      disabled={currentUser?.currentProfile.role === Role.MaintenanceStaff}
                      onChange={(phone) => handleEmergencyContactPhone(phone)}
                      onBlur={handleOnBlurEmergencyContact}
                      openCountryListUpwards={true}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer} sx={{ justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getTypography('Maintenance Contact Details', 600, '18px')}

                  <Tooltip title="Maintenance Contact Details" placement="right">
                    <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                  </Tooltip>
                </Box>
              </Box>
              <Box className={classes.configBodyContainer} sx={{ my: 1 }}>
                <Grid container sx={{ alignItems: 'center', my: 1 }}>
                  <Grid item md={12} lg={7}>
                    {getTypography('Name:', 400, '18px')}
                  </Grid>
                  <Grid item md={12} lg={5}>
                    <FormControl fullWidth error={false}>
                      <InputLabel htmlFor="contact-name" sx={{ marginTop: '-9px', '&.Mui-focused, &.MuiFormLabel-filled': { marginTop: 0 } }}>
                        Contact Name
                      </InputLabel>
                      <OutlinedInput
                        className={classes.inputStyle}
                        id="contact-name"
                        value={contactName}
                        label="Contact Name"
                        onChange={(e) => setContactName(e.target.value)}
                        onBlur={(e) => handleContactName(e.target.value)}
                        aria-describedby="contact-name-helper-text"
                        inputProps={{
                          'aria-label': 'weight',
                        }}
                        disabled={currentUser?.currentProfile.role === Role.MaintenanceStaff}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container sx={{ alignItems: 'center', my: 1 }}>
                  <Grid item md={12} lg={7}>
                    {getTypography('Email Address:', 400, '18px')}
                  </Grid>
                  <Grid item md={12} lg={5}>
                    <FormControl fullWidth error={false}>
                      <InputLabel htmlFor="contact-email" sx={{ marginTop: '-9px', '&.Mui-focused, &.MuiFormLabel-filled': { marginTop: 0 } }}>
                        Email Address
                      </InputLabel>
                      <OutlinedInput
                        className={classes.inputStyle}
                        id="contact-email"
                        value={contactEmail}
                        label="Email Address"
                        onChange={(e) => handleContactEmail(e.target.value)}
                        onBlur={(e) => handleSaveContactEmail(e.target.value)}
                        aria-describedby="contact-email-helper-text"
                        inputProps={{
                          'aria-label': 'weight',
                        }}
                        disabled={currentUser?.currentProfile.role === Role.MaintenanceStaff}
                        error={contactEmailError}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
                <Grid container sx={{ alignItems: 'center', my: 1 }}>
                  <Grid item md={12} lg={7}>
                    {getTypography('Phone Number:', 400, '18px')}
                  </Grid>
                  <Grid item md={12} lg={5}>
                    <PhoneNumberInput
                      value={contactPhone}
                      showErrorMessage={contactPhoneError}
                      disabled={currentUser?.currentProfile.role === Role.MaintenanceStaff}
                      onChange={(phone) => handleContactPhone(phone)}
                      onBlur={handleOnBlurContactPhone}
                      openCountryListUpwards={true}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Box className={classes.configContainer}>
              <Box className={classes.configHeaderContainer} sx={{ justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getTypography('Maintenance Policies', 600, '18px')}

                  <Tooltip
                    title="These policies will be displayed to each user before they are able to create a maintenance request. They range from general information about your policies including emergency contact details and any common issues you do not assist with."
                    placement="right"
                  >
                    <HelpIcon sx={{ fontSize: 16, color: '#C4C4C4', cursor: 'pointer', ml: '10px' }} />
                  </Tooltip>
                </Box>

                {!pmConfig.MaintenancePolicyText && currentUser?.currentProfile.role !== Role.MaintenanceStaff && (
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                    <IconButton
                      onClick={() => {
                        setIsPoliciesModalOpen(true);
                      }}
                      sx={{ p: 0.8, mr: '-8px' }}
                    >
                      <Icon sx={{ width: 26, height: 26 }}>
                        <img
                          style={{
                            cursor: 'pointer',
                            width: 26,
                            height: 26,
                          }}
                          src={AddIcon}
                          alt="Add an item"
                        />
                      </Icon>
                    </IconButton>
                  </Stack>
                )}
              </Box>
              <Box className={classes.configBodyContainer} sx={{ minHeight: 52 }}>
                {pmConfig.MaintenancePolicyText && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                    <div
                      style={{
                        fontFamily: 'Work Sans',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '18px',
                        color: '#14142B',
                        width: 'calc( 100% - 80px )',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                      }}
                      dangerouslySetInnerHTML={{ __html: pmConfig.MaintenancePolicyText ?? '' }}
                    />
                    <Box sx={{ width: 60, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                        <IconButton
                          onClick={() => {
                            setIsPoliciesModalOpen(true);
                          }}
                          sx={{ p: 0.25, mr: '-2px' }}
                        >
                          <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                              style={{
                                cursor: 'pointer',
                                width: '14px',
                                height: '14px',
                              }}
                              src={currentUser?.currentProfile.role === Role.MaintenanceStaff ? ReviewOtpIcon : EditIcon}
                              alt="Edit"
                            />
                          </Icon>
                        </IconButton>
                      </Stack>
                      {currentUser?.currentProfile.role !== Role.MaintenanceStaff && (
                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                          <IconButton
                            onClick={() => {
                              setIsDeletePoliciesModalOpen(true);
                            }}
                            sx={{ p: 0.25 }}
                          >
                            <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <img
                                style={{
                                  cursor: 'pointer',
                                  width: '15px',
                                  height: '18px',
                                }}
                                src={DeleteIcon}
                                alt="Delete"
                              />
                            </Icon>
                          </IconButton>
                        </Stack>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Grid>

          <Backdrop
            sx={{ color: '#fff', zIndex: 30, position: 'absolute', backgroundColor: 'rgb(255, 255, 255, 0.5)' }}
            open={!pmConfig.MaintenanceProductEnabled || !pmConfig.MaintenanceProductPurchased || !pmConfig.MaintenanceMasterFlag}
          ></Backdrop>
        </Grid>
      </Card>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {UpdatingPropertyGroupConfigsError.message}
        </Alert>
      </Snackbar>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={copySnackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="success" sx={{ width: '100%' }}>
          Copied to Clipboard
        </Alert>
      </Snackbar>

      {isCriteriaModalOpen && (
        <MaintenanceCriteriaModal
          open={isCriteriaModalOpen}
          mode={criteriaModalType}
          selectedItem={selectedMEC}
          closeCallback={() => setIsCriteriaModalOpen(false)}
          showSpinnerModal={() => setIsSpinnerModalOpen(true)}
        />
      )}
      {isDeleteCriteriaModalOpen && <DeleteModal open={isDeleteCriteriaModalOpen} confirmCallback={handleConfirmDeleteCriteriaModal} closeCallback={() => setIsDeleteCriteriaModalOpen(false)} />}
      {isPoliciesModalOpen && (
        <MaintenancePoliciesModal
          open={isPoliciesModalOpen}
          description={pmConfig.MaintenancePolicyText ?? ''}
          closeCallback={() => setIsPoliciesModalOpen(false)}
          saveCallback={(text: string) => handleSavePolicies(text)}
          showSpinnerModal={() => setIsSpinnerModalOpen(true)}
        />
      )}
      {isDeletePoliciesModalOpen && <DeleteModal open={isDeletePoliciesModalOpen} confirmCallback={handleConfirmDeletePoliciesModal} closeCallback={() => setIsDeletePoliciesModalOpen(false)} />}
      {isSpinnerModalOpen && (
        <SpinnerModal
          width="472px"
          height="523px"
          isInvisible={true}
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => {
            setIsSpinnerModalOpen(false);
            setSpinnerStatus(null);
          }}
        />
      )}
    </Grid>
  );
}
